import { useState, useEffect } from 'react';
import { ScenarioPresets } from '../shared/ScenarioPresets';
import { useCalculatorStore } from '../../store/calculatorStore';
import { DiscountModifier } from '../shared/DiscountModifier';
import { useAppServiceCalc } from '../../hooks/useCalculate';
import { useAnalytics } from '../../hooks/useAnalytics';
import { FeedbackModal } from '../shared/FeedbackModal';
import { ResourcesSection } from '../shared/ResourcesSection';
import { ShareButton } from '../shared/ShareButton';
import { CurrencyTooltip } from '../shared/CurrencyTooltip';
import { FieldTooltip } from '../shared/FieldTooltip';
import { FormulaDisclosure } from '../shared/FormulaDisclosure';
import { Slider } from '../ui/Slider';

// AppServiceCalc.tsx
// Refactored using established Stitch design patterns
// Converted from Stitch screen: desktop_app_service_view/code.html

export default function AppServiceCalc() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { appServiceInputs, setAppServiceInputs, discount, setDiscount } = useCalculatorStore();
  const { data: calculation, isLoading, isError } = useAppServiceCalc(appServiceInputs);
  const { trackCalculatorUsed } = useAnalytics();
  useEffect(() => {
    if (calculation?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'app-service', result_gbp: calculation.monthly_estimate, tier: appServiceInputs.tier })
    }
  }, [calculation?.monthly_estimate])

  const tiers = [
    { id: 'free', label: 'Free' },
    { id: 'basic', label: 'Basic' },
    { id: 'standard', label: 'Standard' },
    { id: 'premium_v3', label: 'Premium v3' },
    { id: 'premium_v4', label: 'Premium v4' },
  ];

  type AppSku = 'F1'|'B1'|'B2'|'B3'|'S1'|'S2'|'S3'|'P1v3'|'P2v3'|'P3v3'|'P0v4'|'P1v4'|'P2v4'|'P3v4'|'P1mv4'|'P2mv4'|'P3mv4'|'P4mv4'|'P5mv4';

  const SKU_BY_TIER: Record<string, { id: AppSku; label: string; specs: string }[]> = {
    free:       [{ id: 'F1', label: 'F1', specs: 'Shared · 1 GB RAM' }],
    basic:      [
      { id: 'B1', label: 'B1', specs: '1 vCPU · 1.75 GB RAM' },
      { id: 'B2', label: 'B2', specs: '2 vCPU · 3.5 GB RAM' },
      { id: 'B3', label: 'B3', specs: '4 vCPU · 7 GB RAM' },
    ],
    standard:   [
      { id: 'S1', label: 'S1', specs: '1 vCPU · 1.75 GB RAM' },
      { id: 'S2', label: 'S2', specs: '2 vCPU · 3.5 GB RAM' },
      { id: 'S3', label: 'S3', specs: '4 vCPU · 7 GB RAM' },
    ],
    premium_v3: [
      { id: 'P1v3', label: 'P1v3', specs: '2 vCPU · 8 GB RAM' },
      { id: 'P2v3', label: 'P2v3', specs: '4 vCPU · 16 GB RAM' },
      { id: 'P3v3', label: 'P3v3', specs: '8 vCPU · 32 GB RAM' },
    ],
    premium_v4: [
      { id: 'P0v4',  label: 'P0v4',  specs: '1 vCPU · 4 GB RAM' },
      { id: 'P1v4',  label: 'P1v4',  specs: '2 vCPU · 8 GB RAM' },
      { id: 'P2v4',  label: 'P2v4',  specs: '4 vCPU · 16 GB RAM' },
      { id: 'P3v4',  label: 'P3v4',  specs: '8 vCPU · 32 GB RAM' },
      { id: 'P1mv4', label: 'P1mv4', specs: '2 vCPU · 16 GB RAM' },
      { id: 'P2mv4', label: 'P2mv4', specs: '4 vCPU · 32 GB RAM' },
      { id: 'P3mv4', label: 'P3mv4', specs: '8 vCPU · 64 GB RAM' },
      { id: 'P4mv4', label: 'P4mv4', specs: '16 vCPU · 128 GB RAM' },
      { id: 'P5mv4', label: 'P5mv4', specs: '32 vCPU · 256 GB RAM' },
    ],
  };

  const currentTierSkus = SKU_BY_TIER[appServiceInputs.tier] ?? SKU_BY_TIER['premium_v3'];

  const handleOSChange = (os: 'Linux' | 'Windows') => {
    setAppServiceInputs({ os });
  };

  const handleTierChange = (tier: 'free' | 'basic' | 'standard' | 'premium_v3' | 'premium_v4') => {
    const defaultSku = SKU_BY_TIER[tier]?.[0]?.id ?? 'P1v3';
    setAppServiceInputs({ tier, sku: defaultSku });
  };

  const handleSKUChange = (sku: AppSku) => {
    setAppServiceInputs({ sku });
  };

  const handleInstanceCountChange = (instanceCount: number) => {
    setAppServiceInputs({ instanceCount });
  };

  return (
    <div className={`max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
      {/* Left Column: Config */}
      <div className="flex flex-col gap-6">
        <ScenarioPresets presets={[
          {
            label: 'Hobby / low traffic',
            description: 'B1 Basic · Linux · 1 instance — entry-level, personal projects',
            apply: () => { handleTierChange('basic'); setAppServiceInputs({ sku: 'B1', os: 'Linux', instanceCount: 1 }); },
          },
          {
            label: 'Production API',
            description: 'P1v4 Premium · Linux · 2 instances — recommended production tier',
            apply: () => { handleTierChange('premium_v4'); setAppServiceInputs({ sku: 'P1v4', os: 'Linux', instanceCount: 2 }); },
          },
          {
            label: 'Windows .NET app',
            description: 'P2v3 Premium · Windows · 2 instances — shows OS licence cost',
            apply: () => { handleTierChange('premium_v3'); setAppServiceInputs({ sku: 'P2v3', os: 'Windows', instanceCount: 2 }); },
          },
        ]} />
        {/* Operating System Card */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-headline font-bold text-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>terminal</span>
              Operating System
            </h2>
          </div>
          <div className="flex gap-4">
            <button 
              onClick={() => handleOSChange('Linux')}
              className={`flex-1 p-4 rounded border transition-all flex flex-col gap-1 ${appServiceInputs.os === 'Linux' ? 'bg-primary-container/10 border-primary-container' : 'bg-surface-container-lowest border-outline-variant hover:bg-surface-container'}`}
            >
              <div className="flex justify-between items-center w-full">
                <span className={`text-sm font-bold ${appServiceInputs.os === 'Linux' ? 'text-on-surface' : 'text-outline-variant'}`}>Linux</span>
                {appServiceInputs.os === 'Linux' && <span className="bg-[#107c10]/10 text-[#107c10] text-[10px] px-2 py-0.5 rounded border border-[#107c10]/20 font-bold uppercase tracking-wider">Recommended</span>}
              </div>
              <span className="text-[11px] text-outline text-left mt-1 italic opacity-70">~50% lower cost</span>
            </button>
            <button 
              onClick={() => handleOSChange('Windows')}
              className={`flex-1 p-4 rounded border transition-all flex flex-col gap-1 ${appServiceInputs.os === 'Windows' ? 'bg-primary-container/10 border-primary-container' : 'bg-surface-container-lowest border-outline-variant hover:bg-surface-container'}`}
            >
              <div className="flex justify-between items-center w-full">
                <span className={`text-sm font-bold ${appServiceInputs.os === 'Windows' ? 'text-on-surface' : 'text-outline-variant'}`}>Windows</span>
              </div>
              <span className="text-[11px] text-outline text-left mt-1 italic opacity-70">Includes licensing</span>
            </button>
          </div>
        </div>

        {/* Plan Tier Selection */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>layers</span>
            Plan Tier & SKU
          </h2>
          <div className="flex flex-col gap-6">
            <div>
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider mb-3 block">Tier <FieldTooltip content="Basic: dev/test. Standard: production with autoscale. Premium: enhanced performance and VNet. Isolated: dedicated environment." /></label>
              <div className="flex gap-2 flex-wrap">
                {tiers.map(t => (
                  <button
                    key={t.id}
                    onClick={() => handleTierChange(t.id as 'free' | 'basic' | 'standard' | 'premium_v3' | 'premium_v4')}
                    className={`px-4 py-2 text-xs font-semibold rounded border transition-all ${appServiceInputs.tier === t.id ? 'bg-primary-container text-on-primary-container border-primary-container' : 'bg-surface-container-lowest border-outline-variant text-outline hover:border-primary'}`}
                  >
                    {t.label}
                    {t.id === 'standard' && (
                      <span className="ml-1 text-[9px] text-outline/60 normal-case font-normal">(prev gen)</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider mb-3 block">Available SKUs <FieldTooltip content="SKU = Stock Keeping Unit. Each SKU defines the CPU, RAM, and storage included. Higher SKU = more resources and cost." /></label>
              {appServiceInputs.tier === 'premium_v4' && (
                <div className="mb-3 p-3 bg-[#107c10]/10 border border-[#107c10]/40 rounded-sm flex items-start gap-2">
                  <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0 mt-0.5">new_releases</span>
                  <p className="text-[11px] text-on-surface leading-snug">
                    Up to 24% cheaper than Premium v3 · AMD-based · Available in UK South
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {currentTierSkus.map(s => (
                  <button
                    key={s.id}
                    onClick={() => handleSKUChange(s.id)}
                    className={`p-3 rounded border transition-all flex flex-col items-start gap-1 ${appServiceInputs.sku === s.id ? 'bg-primary-container/10 border-primary-container' : 'bg-surface-container-lowest border-outline-variant hover:border-primary'}`}
                  >
                    <span className={`text-sm font-bold ${appServiceInputs.sku === s.id ? 'text-on-surface' : 'text-outline-variant'}`}>{s.label}</span>
                    <span className="text-[10px] text-outline opacity-70 mt-1">{s.specs}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scaling Configuration */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <div className="flex justify-between items-end mb-4">
            <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Instance Count <FieldTooltip content="Number of app instances running in parallel. Each instance is billed separately at the full SKU rate." /></label>
            <span className="text-xl font-bold font-mono text-primary">{appServiceInputs.instanceCount}</span>
          </div>
          <Slider
            value={appServiceInputs.instanceCount || 1}
            onChange={handleInstanceCountChange}
            min={1}
            max={30}
            step={1}
            label="Instance Count"
            formatValue={(n) => `${n}`}
          />
          <div className="mt-6 p-4 bg-surface-container-lowest border border-outline-variant/40 flex items-start gap-3">
            <span className="material-symbols-outlined text-[#e8b000]">warning</span>
            <p className="text-[11px] text-outline leading-tight italic">
              Production apps should use at least 2 instances for high availability and SLA coverage.
            </p>
          </div>
        </div>
      </div>

      {/* Right Column: Results */}
      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">
          {/* Results Card */}
          <div className="bg-surface-container-high micro-gradient border-l-2 border-primary rounded-lg overflow-hidden flex flex-col">
            <div className="p-6">
              <h3 className="font-mono text-[11px] text-outline uppercase tracking-widest mb-2">Estimated Monthly Cost (PAYG)</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold font-headline">
                  {isLoading ? '...' : isError ? 'Error' : `£${calculation?.monthly_estimate.toFixed(2)}`}
                </span>
                {!isLoading && !isError && calculation && <CurrencyTooltip />}
                <span className="text-outline text-sm">/mo</span>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  title="Report an issue with this estimate"
                  className="ml-1 transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>
              <p className="text-[10px] text-outline mt-1 italic uppercase tracking-wider">
                UK South · {appServiceInputs.os} {appServiceInputs.sku}
              </p>
            </div>
            <div className="bg-surface-container-lowest/50 p-6 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">list_alt</span>
                Cost Breakdown
              </h4>
              <ul className="space-y-3">
                {calculation?.breakdown.map((item, idx) => (
                  <li key={idx} className="flex justify-between items-center font-mono text-xs">
                    <span className="text-outline">{item.label}</span>
                    <span className="font-bold">£{item.value.toFixed(2)}</span>
                  </li>
                ))}
                {!calculation && !isLoading && (
                  <p className="text-[10px] text-outline text-center py-4">Adjust configuration to see pricing</p>
                )}
                {calculation && (
                  <li className="pt-3 border-t border-outline-variant flex justify-between items-center font-mono text-sm">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-primary">£{calculation.monthly_estimate.toFixed(2)}</span>
                  </li>
                )}
              </ul>
            </div>
            {calculation && (
              <div className="px-6 pb-2">
                <FormulaDisclosure
                  lines={[
                    ...calculation.breakdown.map((item) => `${item.label}: £${item.value.toFixed(4)}`),
                    `OS: ${appServiceInputs.os} · SKU: ${appServiceInputs.sku} · Instances: ${appServiceInputs.instanceCount} · Region: UK South`,
                  ]}
                  fetchedAt={calculation.prices_last_updated}
                />
              </div>
            )}
            <div className="p-4 bg-surface-container-highest flex gap-2">
              <button className="flex-1 bg-primary text-on-primary py-2.5 font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
                <span className="material-symbols-outlined text-sm">share</span>
                Share
              </button>
              <button className="w-12 bg-surface-container-high border border-outline-variant flex items-center justify-center hover:bg-surface-container-highest transition-colors">
                <span className="material-symbols-outlined">save</span>
              </button>
            </div>
          </div>

          <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 flex gap-3">
            <span className="material-symbols-outlined text-outline text-lg">info</span>
            <p className="text-[10px] text-outline leading-relaxed italic">
              {calculation?.disclaimer || "Premium v3 is the current recommended tier for production workloads. Linux plans are typically 40-60% cheaper than Windows."}
            </p>
          </div>
          {calculation && (
            <DiscountModifier
              discount={discount}
              onDiscountChange={setDiscount}
              paygTotal={calculation.monthly_estimate}
            />
          )}
          <div className="px-0 pb-2">
            <ShareButton inputs={appServiceInputs} result={calculation ?? null} service="app-service" />
          </div>
          <ResourcesSection service="app-service" />
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="app-service"
          estimateValue={calculation ? `£${calculation.monthly_estimate.toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </div>
  );
}
